import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { getDiscounts } from '@/api/clarityLens'
import { ScalrTableColumn } from '@/components/customComponents'
import { formatMetric } from '@/hooks/clarity/util'
import { pushNotification, useAppDispatch } from '@/store'
import { areFilterParamsValid } from '@/utils/filterParams'
import {
  DiscountItemResponseModel,
  DiscountsItemModel,
} from '@/types/clarityLens'

type ChartDataModel = {
  data: Record<string, number>
  labels: string[]
}

const BASE_DISCOUNTS_ITEMS_COLUMNS: ScalrTableColumn[] = [
  {
    header: 'Discount Type',
    accessorKey: 'discountType',
    type: 'text',
  },
  {
    header: '# of Discounts Applied',
    accessorKey: 'numberOfDiscountsApplied',
    type: 'number',
  },
  {
    header: 'Total Discount Amount',
    accessorKey: 'totalDiscountAmount',
    type: 'money',
  },
  {
    header: 'Avg Profit Per Discounted Txn',
    accessorKey: 'avgProfitPerDiscountedTxn',
    type: 'money',
  },
  {
    header: 'Discount %',
    accessorKey: 'discountPercentage',
    type: 'percentage',
  },
]

export default function useDiscountsItems(
  startDate: string,
  endDate: string,
  branches: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
) {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [isFetching, setIsFetching] = useState<boolean>(false)

  const isReady = useMemo(
    () =>
      areFilterParamsValid(startDate, endDate, branches) &&
      Boolean(itemGroupName?.trim()),
    [startDate, endDate, branches, itemGroupName]
  )
  const loading = !isReady || isFetching

  const [discountsItemsData, setDiscountsItemsData] =
    useState<DiscountsItemModel[]>()
  const [categoryByCountChartData, setCategoryByCountChartData] =
    useState<ChartDataModel>()
  const [categoryByAmountChartData, setCategoryByAmountChartData] =
    useState<ChartDataModel>()

  const fetchAllData = useCallback(async () => {
    if (!isReady) {
      setIsFetching(false)
      return
    }
    setIsFetching(true)
    try {
      const { tableData, categoryByCountChartData, categoryByAmountChartData } =
        await fetchDiscountsItemsData(
          branches,
          startDate,
          endDate,
          itemGroupName,
          sizeFilter,
          temperatureFilter
        )

      setDiscountsItemsData(tableData)
      setCategoryByCountChartData(categoryByCountChartData)
      setCategoryByAmountChartData(categoryByAmountChartData)
    } catch (err) {
      const errorInstance = new Error(
        err instanceof Error ? err.message : 'An unknown error occurred'
      )

      dispatch(
        pushNotification({
          type: 'error',
          title: t('common.errors.serverError'),
          description: errorInstance.message,
          duration: 5000,
        })
      )
    } finally {
      setIsFetching(false)
    }
  }, [
    isReady,
    branches,
    startDate,
    endDate,
    itemGroupName,
    sizeFilter,
    temperatureFilter,
  ])

  const refetch = useCallback(() => {
    console.log('Refetching discounts items...')
    return fetchAllData()
  }, [fetchAllData])

  useEffect(() => {
    fetchAllData()
  }, [fetchAllData])

  return {
    discountsItemsData,
    BASE_DISCOUNTS_ITEMS_COLUMNS,
    categoryByCountChartData,
    categoryByAmountChartData,
    loading,
    refetch,
  }
}

async function fetchDiscountsItemsData(
  branches: string,
  startDate: string,
  endDate: string,
  itemGroupName: string,
  sizeFilter: string,
  temperatureFilter: string
): Promise<{
  tableData: DiscountsItemModel[]
  categoryByCountChartData: ChartDataModel
  categoryByAmountChartData: ChartDataModel
}> {
  try {
    const response = await getDiscounts(
      branches,
      startDate,
      endDate,
      itemGroupName,
      sizeFilter,
      temperatureFilter
    )

    const tableData: DiscountsItemModel[] = Object.entries(
      response.discounts
    ).map(([keyName, value]) => {
      return convertToDiscountsItemModel(
        keyName,
        value as unknown as DiscountItemResponseModel
      )
    })

    // Convert categoryByCount to pie chart data
    const categoryByCountChartData: ChartDataModel = {
      data: {
        operational: response.categoryByCount.Operational,
        loyalty: response.categoryByCount.Loyalty,
        uncategorized: response.categoryByCount.Uncategorized,
      },
      labels: ['Operational', 'Loyalty', 'Uncategorized'],
    }

    // Convert categoryByAmount to pie chart data
    const categoryByAmountChartData: ChartDataModel = {
      data: {
        operational: response.categoryByAmount.Operational,
        loyalty: response.categoryByAmount.Loyalty,
        uncategorized: response.categoryByAmount.Uncategorized,
      },
      labels: ['Operational', 'Loyalty', 'Uncategorized'],
    }

    return {
      tableData,
      categoryByCountChartData,
      categoryByAmountChartData,
    }
  } catch (err) {
    throw new Error(
      err instanceof Error ? err.message : 'An unknown error occurred'
    )
  }
}

function convertToDiscountsItemModel(
  keyName: string,
  value: DiscountItemResponseModel
): DiscountsItemModel {
  const {
    count,
    amount,
    avg_profit_per_discounted_txn,
    discountPercentage,
    category,
  } = value

  const baseModel: DiscountsItemModel = {
    discountType: keyName,
    numberOfDiscountsApplied: formatMetric(count, 0),
    totalDiscountAmount: formatMetric(amount, 2),
    avgProfitPerDiscountedTxn: formatMetric(avg_profit_per_discounted_txn, 2),
    // TODO: Remove * 10 when API is updated
    discountPercentage: formatMetric(discountPercentage * 10, 2),
    category: category,
  }

  return baseModel
}
